'use client';

import {
  ActionIcon,
  AppShell,
  Box,
  Flex,
  Group,
  Modal,
  Text,
  TextInput,
  Title,
  Tooltip,
} from '@mantine/core';
import HelpOutline from '@material-design-icons/svg/filled/help_outline.svg';
import MenuIcon from '@material-design-icons/svg/filled/menu.svg';
import Image from 'next/image';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useEffect, useState } from 'react';

import { SLOGAN_MAX_LENGTH } from '@/constants';
import { useAuth, useHeader, usePageTitle } from '@/contexts';

import {
  DEFAULT_ISSUE_FORM_DATA,
  getFromLocalStorage,
  ISSUE_FORM_DATA_KEY,
  IssueFormData,
  saveToLocalStorage,
} from '../IssuePageContent/IssuePageContent';

import styles from './AppHeader.module.css';

interface AppHeaderProps {
  showOnlyIfNotLanding?: boolean;
  toggleNavBarIsOpen: () => void;
}

const AppHeader: React.FC<AppHeaderProps> = ({
  showOnlyIfNotLanding,
  toggleNavBarIsOpen,
}) => {
  const pathname = usePathname();
  const { secondaryTitle } = usePageTitle();
  const { isLoggedIn, currentEntityId } = useAuth();
  const {
    showSloganInput,
    setSecondaryTitle,
    isSloganEditable,
    isPreviewSlogan,
    infoModalOpened,
    openInfoModal,
    closeInfoModal,
    refreshSlogan,
    setRefreshSlogan,
  } = useHeader();

  const [localTitle, setLocalTitle] = useState(secondaryTitle ?? '');

  useEffect(() => {
    setLocalTitle(secondaryTitle ?? '');
  }, [secondaryTitle]);

  useEffect(() => {
    // Load the slogan from localStorage when the component is in edit mode
    if (isSloganEditable) {
      const storedData: IssueFormData = getFromLocalStorage(
        ISSUE_FORM_DATA_KEY,
        DEFAULT_ISSUE_FORM_DATA,
      );
      if (storedData.slogan) {
        setLocalTitle(storedData.slogan);
        setSecondaryTitle(storedData.slogan);
      }
      setRefreshSlogan(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSlogan]);

  // Don't show header on landing page if showOnlyIfNotLanding is true
  if (showOnlyIfNotLanding && pathname === '/') {
    return null;
  }

  // Determine logo link based on authentication status
  const logoLinkPath =
    isLoggedIn && currentEntityId ? `/profiles/${currentEntityId}` : '/';

  return (
    <AppShell.Header classNames={{ header: styles.header }}>
      <Flex
        justify="space-between"
        align="center"
        className={styles.headerContent}
      >
        {/* Logo and Page Title Section */}
        <Flex
          align="center"
          gap="lg"
        >
          <Link
            href={logoLinkPath}
            className={styles.logoLink}
          >
            <Box style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <Image
                src="/assets/logo.svg"
                alt="Sway Logo"
                width={50}
                height={50}
                className={styles.logo}
              />
              <Text
                fw={500}
                size="sm"
                c="neutral.9"
                style={{ marginBottom: 40 }}
              >
                beta
              </Text>
            </Box>
          </Link>

          {/* Slogan Section */}
          <Box style={{ flexGrow: 1 }}>
            {(showSloganInput || isSloganEditable) && !isPreviewSlogan ? (
              <Tooltip
                variant="primary-light"
                label="Write a slogan that turns your topic into a movement people want to join."
                opened={isSloganEditable && !localTitle}
                position="bottom-start"
                offset={10}
                arrowOffset={10}
                arrowSize={15}
                withArrow
                multiline
              >
                <TextInput
                  placeholder="Add a slogan"
                  value={localTitle}
                  disabled={!isSloganEditable}
                  onChange={(event) => {
                    const newValue = event.currentTarget.value.slice(
                      0,
                      SLOGAN_MAX_LENGTH,
                    );
                    setLocalTitle(newValue);
                    setSecondaryTitle(newValue);

                    // Update localStorage with the new slogan
                    if (isSloganEditable) {
                      saveToLocalStorage(ISSUE_FORM_DATA_KEY, {
                        ...getFromLocalStorage(ISSUE_FORM_DATA_KEY, {}),
                        slogan: newValue,
                      });
                    }
                  }}
                  maxLength={SLOGAN_MAX_LENGTH}
                  rightSection={
                    <Text
                      size="xs"
                      c="dimmed"
                    >
                      {localTitle.length}/{SLOGAN_MAX_LENGTH}
                    </Text>
                  }
                />
              </Tooltip>
            ) : (
              (localTitle || secondaryTitle) && (
                <Title order={4}>{localTitle || secondaryTitle}</Title>
              )
            )}
          </Box>
        </Flex>

        {/* Navigation and Help Icons */}
        <Group
          gap="sm"
          align="center"
          wrap="nowrap"
        >
          <Tooltip
            label="Info"
            visibleFrom="sm"
          >
            <ActionIcon
              onClick={openInfoModal}
              variant="subtle"
              aria-label="Info"
            >
              <HelpOutline />
            </ActionIcon>
          </Tooltip>
          <Tooltip
            label="Menu"
            visibleFrom="sm"
          >
            <ActionIcon
              onClick={toggleNavBarIsOpen}
              variant="subtle"
              aria-label="Toggle navigation menu"
            >
              <MenuIcon />
            </ActionIcon>
          </Tooltip>
        </Group>
      </Flex>

      {/* Info Modal */}
      <Modal
        opened={infoModalOpened}
        onClose={closeInfoModal}
        title="What is Sway?"
        styles={{
          header: {
            backgroundColor: 'var(--mantine-primary-9)',
            margin: 'var(--mantine-spacing-md)',
          },
          body: {
            margin: 'var(--mantine-spacing-md)',
          },
        }}
        overlayProps={{
          backgroundOpacity: 0.55,
          blur: 3,
        }}
        centered
      >
        <Text size="md">
          Back Leaders who champion your causes. When you{' '}
          <b>&ldquo;Vote with&rdquo;</b> them, you...
        </Text>
        <ul>
          <li>Let them advocate for issues that matter most to you.</li>
          <li>Boost their influence by verifying your voter registration.</li>
          <li>Get personalized voter guides.</li>
        </ul>
        <Text size="md">
          Your support stays <b>private</b> and <b>secure</b>, unless you chose
          otherwise — just like your vote.
        </Text>
      </Modal>
    </AppShell.Header>
  );
};

export { AppHeader };
